import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Icon } from './ui/Icon.js';

interface AllergenEntry {
  id: string;
  allergen: string;
  safe: boolean;
  introducedAt: string;
}

const TOP_9 = ['milk','eggs','peanuts','tree_nuts','wheat','soy','sesame','fish','shellfish'] as const;
type Allergen = typeof TOP_9[number];

const ALLERGEN_LABEL: Record<Allergen, string> = {
  milk: 'Milk', eggs: 'Eggs', peanuts: 'Peanuts', tree_nuts: 'Tree nuts',
  wheat: 'Wheat', soy: 'Soy', sesame: 'Sesame', fish: 'Fish', shellfish: 'Shellfish',
};

const ALLERGEN_ICON: Record<Allergen, string> = {
  milk: '🥛', eggs: '🥚', peanuts: '🥜', tree_nuts: '🌰',
  wheat: '🌾', soy: '🫘', sesame: '⚪', fish: '🐟', shellfish: '🦐',
};

const SAFE_COLOR = 'oklch(0.74 0.14 162)';

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

export function AllergenTrackerSection() {
  const qc = useQueryClient();

  const { data: log = [] } = useQuery<AllergenEntry[]>({
    queryKey: ['allergen-log'],
    queryFn: () => api.get('/api/allergen-log'),
    staleTime: 5 * 60_000,
  });

  const logAllergen = useMutation({
    mutationFn: ({ allergen, safe }: { allergen: Allergen; safe: boolean }) =>
      api.post('/api/allergen-log', { allergen, safe }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allergen-log'] }),
  });

  const deleteAllergen = useMutation({
    mutationFn: (allergen: Allergen) => api.delete(`/api/allergen-log/${allergen}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['allergen-log'] }),
  });

  const loggedMap = Object.fromEntries(log.map((e) => [e.allergen, e]));
  const introducedCount = log.length;
  const safeCount = log.filter((e) => e.safe).length;
  const reactionCount = log.filter((e) => !e.safe).length;

  return (
    <div className="card p-6" id="allergens">
      <div className="mb-3">
        <div className="flex items-center gap-2" style={{ color: 'var(--ink-3)' }}>
          <Icon name="shield" size={14} />
          <span className="text-xs font-semibold uppercase" style={{ letterSpacing: '0.12em' }}>
            Allergen tracker
          </span>
        </div>
        <p className="text-xs mt-1" style={{ color: 'var(--ink-4)' }}>
          Log each allergen as your family tries it. {introducedCount} of {TOP_9.length} introduced
          {introducedCount > 0 && (
            <>
              {' · '}{safeCount} safe{reactionCount > 0 && <> · {reactionCount} reaction</>}
            </>
          )}
        </p>
      </div>

      <div
        className="flex items-start gap-3 px-3 py-2 mb-4"
        style={{
          background: 'color-mix(in oklab, var(--warning) 10%, var(--surface))',
          border: '1px solid color-mix(in oklab, var(--warning) 30%, transparent)',
          borderRadius: 'var(--r-sm)',
        }}
      >
        <Icon name="shield" size={13} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 2 }} />
        <p style={{ color: 'var(--ink-2)', fontSize: 12.5, margin: 0 }}>
          Always consult your pediatrician before introducing allergens to infants or those with known food allergies.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {TOP_9.map((allergen) => {
          const entry = loggedMap[allergen] as AllergenEntry | undefined;
          const status: 'safe' | 'reaction' | 'untried' =
            entry ? (entry.safe ? 'safe' : 'reaction') : 'untried';
          const borderLeft =
            status === 'safe' ? `3px solid ${SAFE_COLOR}`
            : status === 'reaction' ? '3px solid var(--danger)'
            : undefined;
          return (
            <div
              key={allergen}
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--line-soft)',
                borderRadius: 'var(--r-md)',
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                borderLeft,
              }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div style={{ fontSize: 24, lineHeight: 1 }} aria-hidden="true">{ALLERGEN_ICON[allergen]}</div>
                  <div style={{ marginTop: 6, fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                    {ALLERGEN_LABEL[allergen]}
                  </div>
                </div>
                <StatusBadge status={status} />
              </div>

              {entry ? (
                <div>
                  <p style={{ color: 'var(--ink-4)', fontSize: 12, margin: 0 }}>
                    Introduced {new Date(entry.introducedAt).toLocaleDateString()}
                  </p>
                  {daysSince(entry.introducedAt) < 7 && (
                    <p style={{ color: 'var(--warning)', fontSize: 11, margin: '4px 0 0' }}>
                      Introduced {daysSince(entry.introducedAt)} day{daysSince(entry.introducedAt) === 1 ? '' : 's'} ago — watch for delayed reactions.
                    </p>
                  )}
                </div>
              ) : (
                <p style={{ color: 'var(--ink-4)', fontSize: 12, margin: 0 }}>Not yet introduced</p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => logAllergen.mutate({ allergen, safe: true })}
                  disabled={logAllergen.isPending}
                  aria-label={`Mark ${ALLERGEN_LABEL[allergen]} as safe`}
                  className="btn"
                  style={{
                    flex: 1,
                    minHeight: 40,
                    borderRadius: 'var(--r-sm)',
                    border: `1px solid color-mix(in oklab, ${SAFE_COLOR} 40%, transparent)`,
                    color: SAFE_COLOR,
                    background: 'transparent',
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  ✓ Safe
                </button>
                <button
                  type="button"
                  onClick={() => logAllergen.mutate({ allergen, safe: false })}
                  disabled={logAllergen.isPending}
                  aria-label={`Mark ${ALLERGEN_LABEL[allergen]} as reaction`}
                  className="btn"
                  style={{
                    flex: 1,
                    minHeight: 40,
                    borderRadius: 'var(--r-sm)',
                    border: '1px solid color-mix(in oklab, var(--danger) 40%, transparent)',
                    color: 'var(--danger)',
                    background: 'transparent',
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  ✗ Reaction
                </button>
              </div>

              {entry && (
                <button
                  type="button"
                  onClick={() => deleteAllergen.mutate(allergen)}
                  disabled={deleteAllergen.isPending}
                  className="self-start"
                  style={{
                    color: 'var(--ink-4)',
                    fontSize: 11,
                    background: 'transparent',
                    border: 'none',
                    padding: '4px 0',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Reset
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'safe' | 'reaction' | 'untried' }) {
  if (status === 'safe') {
    return (
      <span
        className="chip"
        style={{
          background: `color-mix(in oklab, ${SAFE_COLOR} 14%, transparent)`,
          color: SAFE_COLOR,
          borderColor: 'transparent',
          fontSize: 11,
          padding: '3px 9px',
        }}
      >
        Safe
      </span>
    );
  }
  if (status === 'reaction') {
    return (
      <span
        className="chip"
        style={{
          background: 'color-mix(in oklab, var(--danger) 14%, transparent)',
          color: 'var(--danger)',
          borderColor: 'transparent',
          fontSize: 11,
          padding: '3px 9px',
        }}
      >
        Reaction
      </span>
    );
  }
  return (
    <span
      className="chip"
      style={{
        background: 'var(--surface-2)',
        color: 'var(--ink-4)',
        borderColor: 'var(--line-soft)',
        fontSize: 11,
        padding: '3px 9px',
      }}
    >
      Not introduced
    </span>
  );
}
